/**
 * The stand-alone chat member overflow menu's by-role table, mirroring
 * `apps/app/src/features/poolMembers/memberOverflowMenu.ts`'s shape. A
 * client-side permission table, decided here and never inside a component.
 * No id returned here is wired to an endpoint by this file; `ChatMembersDialog.tsx`
 * does that wiring.
 *
 * `/chat/list-members` returns `role: "commissioner" | "admin" | "member"` on
 * the wire because it serves pool chats too. `isChatAdminRole` treats
 * `commissioner` as an admin role for menu purposes; the word itself must
 * never render on a stand-alone chat surface (`52-standalone-chats.md`,
 * "Don'ts", first line).
 */
import {
  END_TIMEOUT_ACTION_LABEL,
  timeoutActionFor,
} from "./memberTimeoutLogic";
import {
  MEMBER_BLOCK_LABEL,
  MEMBER_MAKE_ADMIN_LABEL,
  MEMBER_REMOVE_LABEL,
  MEMBER_REPORT_LABEL,
} from "./standaloneChatCopy";

export type typeChatMemberMenuActionId =
  | "removeFromChat"
  | "makeAdmin"
  | "timeout"
  | "endTimeout"
  | "block"
  | "report";

export type typeChatMemberMenuItem = {
  id: typeChatMemberMenuActionId;
  label: string;
  destructive: boolean;
};

export type typeChatMemberRole = "commissioner" | "admin" | "member";

const MENU_LABELS: Record<typeChatMemberMenuActionId, string> = {
  removeFromChat: MEMBER_REMOVE_LABEL,
  makeAdmin: MEMBER_MAKE_ADMIN_LABEL,
  timeout: "Time out…",
  endTimeout: END_TIMEOUT_ACTION_LABEL,
  block: MEMBER_BLOCK_LABEL,
  report: MEMBER_REPORT_LABEL,
};

const DESTRUCTIVE_IDS: readonly typeChatMemberMenuActionId[] = [
  "removeFromChat",
  "block",
  "report",
];

function toMenuItems(
  ids: readonly typeChatMemberMenuActionId[],
): typeChatMemberMenuItem[] {
  return ids.map((id) => ({
    id,
    label: MENU_LABELS[id],
    destructive: DESTRUCTIVE_IDS.includes(id),
  }));
}

export function isChatAdminRole(role: typeChatMemberRole): boolean {
  return role === "admin" || role === "commissioner";
}

/**
 * True when the viewer's own entry in `members` is an admin role and no
 * OTHER entry is. Used to gate the leave path before `/chat/leave` is ever
 * called, and to decide whether to show the transfer-first prompt.
 */
export function isSoleAdmin(input: {
  viewerUserId: string;
  members: readonly { userId: string; role: typeChatMemberRole }[];
}): boolean {
  const viewerEntry = input.members.find(
    (member) => member.userId === input.viewerUserId,
  );
  if (viewerEntry === undefined || !isChatAdminRole(viewerEntry.role)) {
    return false;
  }
  return !input.members.some(
    (member) =>
      member.userId !== input.viewerUserId && isChatAdminRole(member.role),
  );
}

/**
 * ISSUE-945. True when the viewer is the chat's only active member.
 * `/chat/list-members` returns active members only and pages at 30, so one
 * item on the first page means one member. Such a viewer is always the sole
 * Admin too, but has nobody to hand the role to.
 */
export function isOnlyMember(input: {
  viewerUserId: string;
  members: readonly { userId: string }[];
}): boolean {
  return (
    input.members.length === 1 &&
    input.members[0]?.userId === input.viewerUserId
  );
}

export function buildChatMemberOverflowMenu(input: {
  viewerUserId: string;
  viewerRole: typeChatMemberRole;
  targetUserId: string;
  targetRole: typeChatMemberRole;
  /** ISSUE-1364. The target's running timeout, as /chat/list-members has it. */
  targetTimedOutUntilIso?: string | null | undefined;
  now?: Date | undefined;
}): typeChatMemberMenuItem[] {
  // Leaving is a dialog-level action, not a self overflow row.
  if (input.targetUserId === input.viewerUserId) return [];

  if (!isChatAdminRole(input.viewerRole)) {
    return toMenuItems(["block", "report"]);
  }

  if (isChatAdminRole(input.targetRole)) {
    // Nothing to promote; another Admin is already one.
    return toMenuItems(["removeFromChat", "block", "report"]);
  }

  const timeoutAction = timeoutActionFor({
    viewerUserId: input.viewerUserId,
    canModerate: true,
    targetUserId: input.targetUserId,
    targetRole: input.targetRole,
    targetTimedOutUntilIso: input.targetTimedOutUntilIso,
    now: input.now ?? new Date(),
  });
  return toMenuItems([
    "removeFromChat",
    "makeAdmin",
    ...(timeoutAction === null ? [] : [timeoutAction]),
    "block",
    "report",
  ]);
}
